<script setup>
import { ref, computed, reactive } from "vue";
import { Link, router } from "@inertiajs/vue2";
import { hasPermissionTo } from "../../Composables/usePermissions.js";
import SimpleForm from "../Form/SimpleForm.vue";
import LessonAttendanceNotes from "./LessonAttendanceNotes.vue";

// Props

const props = defineProps({
    auth: {
        type: Object,
        required: true,
    },
    lesson: {
        type: Object,
        required: true,
    },
    billingUser: {
        type: Object,
        required: true,
    },
    purchaseOption: {
        type: Object,
        default: () => ({}),
        required: false,
    },
    paymentStatus: {
        type: String,
        default: "",
        required: false,
    },
});

// Data
const showLessonOptions = ref(false);
const snack = ref(null);

// Emits

const emit = defineEmits(["reset", "success", "rescheduleLesson"]);

// Computed

const showVisitStore = computed(() => {
    return props.auth.staffView && props.paymentStatus == "Unpaid";
});

const showPayNow = computed(() => {
    return props.auth.staffView && props.paymentStatus == "Past Due";
});

const canDeleteInSeries = computed(() => {
    if (!props.lesson.series) return false;
    return props.lesson.series.n == 1;
});

const isRepeating = computed(() => {
    if (!props.lesson.recurrence_id) return false;
    if (props.lesson.type == "Assessment") return false;
    return true;
});

const hasStudents = computed(() => {
    return !!props.lesson.students.length;
});

const isGroupEvent = computed(() => {
    return props.lesson.group_event;
});

const isOnline = computed(() => {
    return props.lesson.online;
});

const isCanceled = computed(() => {
    return props.lesson.cancelled;
});

const showJoinMeeting = computed(() => {
    return (
        isOnline.value && props.lesson.teacher.zoom_url && !getLessonNote.value
    );
});

const hasAttendanceStatus = computed(() => {
    if (!props.lesson.students.length) return false;
    return Object.values(props.lesson.students).some(
        (e) => e.attendance.status == "Attended"
    );
});

const hasRescheduleMinutes = computed(() => {
    return props.lesson.student?.attendance?.reschedule_minutes;
});

const hasReschedulePass = computed(() => {
    return props.lesson.student?.attendance?.reschedule_pass;
});

const canToggleOnline = computed(() => {
    if (!hasStudents.value) return false;
    if (isCanceled.value) return false;
    if (hasAttendanceStatus.value) return false;
    if (props.lesson.availability?.online_only) return false;
    return true;
});

const canDelete = computed(() => {
    if (canDeleteInSeries.value) return true; //if event is part of a series, is it the first one: delete enabled
    if (!props.lesson.students.length) return true; // if no students, it's either a group event or hold: delete enabled
    // only managers can delete assessments
    if (
        props.lesson.type == "Assessment" ||
        props.lesson.type == "Reschedule" ||
        props.lesson.type == "Drop-In"
    ) {
        if (!hasPermissionTo("Book student assessments")) return false;
        // enabled for managers
        return true;
    }
    if (
        !Object.values(props.lesson.students).some((e) => e.attendance.status)
    ) {
        // if there are students and no attendance taken: delete enabled
        return true;
    }
    return false;
});

const canReschedule = computed(() => {
    if (props.lesson.student?.attendance.status == "Attended") {
        return false;
    }
    if (isGroupEvent.value) {
        if (isCanceled.value) {
            return (
                props.lesson.series.n >= props.lesson.group_event.occurrences ||
                true
            );
        }
        if (hasStudents.value) return false;
    }

    if (!props.lesson.student) return false;

    const inRescheduleWindow = moment(props.lesson.starts_at).isBetween(
        moment().add(props.lesson.rescheduleWindow, "hours"),
        moment().add(2, "weeks")
    );

    if (
        !inRescheduleWindow &&
        !isCanceled.value &&
        !hasPermissionTo("Schedule within 24 hours") &&
        !hasPermissionTo("Schedule outside of two weeks")
    ) {
        return false;
    }

    if (!isCanceled.value) return true;

    return hasRescheduleMinutes.value || hasReschedulePass.value;
});

const canEditPlaceholders = computed(() => {
    if (!hasPermissionTo("Book student assessments")) return false;
    if (!props.lesson?.id) return false;
    if (props.lesson.type !== "Assessment") return false;
    if (props.lesson.cancelled) return false;
    return true;
});

const getLessonNote = computed(() => {
    if (!props.lesson) return null;
    if (props.lesson.type == "Hold" || isGroupEvent.value) return null;
    return props.lesson?.student?.attendance.lesson_note;
});

const getPracticeNote = computed(() => {
    return props.lesson?.student?.attendance.practice_note;
});

const hasContextButtons = computed(() => {
    if (showVisitStore.value || showPayNow.value) {
        return true;
    }

    if (showJoinMeeting.value) {
        return true;
    }
    if (getLessonNote.value) return true;
    return false;
});

const actions = computed(() => {
    return [
        {
            title: "Visit Store",
            icon: "local_offer",
            color: "accent",
            link: route("store", {
                tab: "frequent",
                user: props.billingUser.id,
            }),
            method: () => null,
            hidden: () => !showVisitStore.value,
            disabled: () => false,
        },
        {
            title: "Pay Now",
            icon: "payments",
            color: "soli-status-red",
            link: route("invoice.past_due", {
                subscription_id: props.purchaseOption?.pastDue,
                user_id: props.purchaseOption?.user?.id,
            }),
            method: () => null,
            hidden: () => !showPayNow.value,
            disabled: () => false,
        },
        {
            title: "Reschedule",
            icon: "change_circle",
            color: "accent",
            link: null,
            method: () => rescheduleLesson(),
            hidden: () =>
                hasContextButtons.value || props.lesson.type == "Hold",
            disabled: () => !canReschedule.value,
        },
        {
            title: "Delete",
            icon: "delete",
            color: "soli-status-red",
            link: null,
            method: () => (forms.confirmDelete.show = true),
            hidden: () =>
                hasContextButtons.value || props.lesson.type != "Hold",
            disabled: () => !canDelete.value,
        },
        /*{
            title: "Move Online",
            icon: "videocam",
            color: "accent",
            link: null,
            method: () => (forms.toggleOnline.show = true),
            hidden: () => hasContextButtons.value || isGroupEvent.value,
            disabled: () => !canToggleOnline.value,
        },*/
        {
            title: "View Notes",
            icon: "notes",
            color: "accent",
            link: null,
            method: () => {
                if (getLessonNote.value) {
                    showAttendanceNotes.value = true;
                    return;
                }
                if (props.lesson.type == "Weekly Lesson") {
                    return window.open(
                        `/profile/${props.lesson?.student?.id}/attendance?viewNotes=1`,
                        "_blank"
                    );
                }
            },
            hidden: () =>
                !getLessonNote.value && props.lesson.type !== "Weekly Lesson",
            disabled: () => false,
        },
        {
            title: "Join Meeting",
            icon: "videocam",
            color: "accent",
            link: props.lesson.teacher.zoom_url,
            method: () => null,
            hidden: () => !showJoinMeeting.value,
            disabled: () => false,
        },
    ];
});

const menuItems = computed(() => {
    return [
        {
            title: "Reschedule",
            method: () => rescheduleLesson(),
            disabled: () => !canReschedule.value,
        },
        {
            title: "Delete",
            method: () => (forms.confirmDelete.show = true),
            disabled: () => !canDelete.value,
        },
        {
            title: `Move ${isOnline.value ? "In-studio" : "Online"}`,
            method: () => (forms.toggleOnline.show = true),
            disabled: () => !canToggleOnline.value,
        },
        {
            title: "Substitute Teacher",
            method: () => (forms.substituteTeacher.show = true),
            disabled: () =>
                isCanceled.value ||
                !isGroupEvent.value ||
                hasAttendanceStatus.value ||
                !hasPermissionTo("Substitute teachers"),
        },
        {
            title: "Edit Capacity",
            method: () => (forms.editCapacity.show = true),
            disabled: () =>
                !isGroupEvent.value ||
                isCanceled.value ||
                hasAttendanceStatus.value ||
                !hasPermissionTo("Substitute teachers"), // TODO replace this with a more specific permission check
        },
        {
            title: `${
                props.lesson.hasPlaceholders ? "Clear" : "Generate"
            } Placeholders`,
            method: () => (forms.editPlaceholders.show = true),
            disabled: () => !canEditPlaceholders.value,
        },
        {
            title: "Copy Booking Link",
            method: () =>
                copyToClipboard(
                    props.lesson.event_register_id
                        ? route(
                              "eventRegistration.create",
                              props.lesson.event_register_id
                          )
                        : route("classRegistration.create", props.lesson.id)
                ),
            disabled: () =>
                props.lesson.group_event?.title !== "Musicologie Junior" &&
                !props.lesson.event_register_id,
        },
    ];
});

// Reactive State

const showAttendanceNotes = ref(false);

const forms = reactive({
    confirmDelete: {
        show: false,
        title: () => {
            if (isRepeating.value) return "What would you like to delete?";
            return "Are you sure?";
        },
        subtitle: () => {
            if (["Hold", "Pending"].includes(props.lesson.type)) return "";
            const firstName = props.lesson.student?.first_name;
            if (isRepeating.value) {
                if (isGroupEvent.value) {
                    return "Students won't be notified of this change. Membership passes will be returned to student accounts for future booking.";
                }
                return `${firstName} won't be notified of this change. This membership pass will be returned to ${firstName}'s account for future booking.`;
            }
            if (isGroupEvent.value) {
                return "Students will not be notified and will be unable to reschedule this lesson. Passes will be returned to student accounts for future booking. This is not recommended, consider canceling this lesson instead.";
            }
            return `${firstName} won't be notified and will be unable to reschedule this lesson. Passes will be returned to ${firstName}'s account. This is not recommended, consider canceling this lesson instead.`;
        },
        submit: {
            url: route("lesson.delete", props.lesson.id),
            method: "put",
            data: { cancelRepeating: isRepeating.value },
            close: () => {
                forms.confirmDelete.show = false;
            },
        },
        slot: isRepeating.value ? "confirmDelete" : null,
    },
    substituteTeacher: {
        show: false,
        title: () => "Substitute Teacher",
        subtitle: () =>
            "Replace current teacher for this event. This change will not affect future events in this series (if any).",
        submit: {
            url: route("lesson.update", props.lesson.id),
            method: "put",
            data: { user_id: null },
            close: () => {
                forms.substituteTeacher.show = false;
            },
        },
        slot: "substituteTeacher",
    },
    toggleOnline: {
        show: false,
        title: () => "Are you sure?",
        subtitle: () =>
            `This lesson will be moved ${
                isOnline.value ? "in-person" : "online"
            } and your student(s) will be notified.`,
        submit: {
            url: `/lesson/${props.lesson.id}`,
            method: "put",
            data: { online: !isOnline.value },
            close: () => {
                forms.toggleOnline.show = false;
            },
        },
        slot: null,
    },
    editCapacity: {
        show: false,
        title: () => "Edit Capacity",
        subtitle: () => "Change the capacity of this event.",
        submit: {
            url: route("lesson.update", props.lesson.id),
            method: "put",
            data: { capacity: props.lesson.capacity },
            close: () => {
                forms.editCapacity.show = false;
            },
        },
        slot: "editCapacity",
    },
    editPlaceholders: {
        show: false,
        title: () => "Are you sure?",
        subtitle: () =>
            `This will ${
                props.lesson.hasPlaceholders ? "clear" : "generate"
            } placeholder lessons for this assessment. Placeholder lessons reserve this weekly lesson time for the next two weeks.`,
        submit: {
            url: route("lesson.update", props.lesson.id),
            method: "put",
            data: { clearPlaceholders: Number(props.lesson.hasPlaceholders) },
            close: () => {
                forms.editPlaceholders.show = false;
            },
        },
        slot: "editPlaceholders",
    },
});

// Methods

function rescheduleLesson() {
    const item = Object.fromEntries(Object.entries(props.lesson));
    item.date = new Date(item.starts_at);
    item.resource = item.room;
    item.attendance = item.student?.attendance ?? "Teacher-cancel";
    item.type = {
        name: item.type == "Weekly Lesson" ? "Reschedule" : item.type,
        originalType: item.type == "Reschedule" ? "Weekly Lesson" : item.type,
        reschedule: true,
        disabled: false,
        recurring: false,
    };
    item.student = props.lesson.student;
    emit("rescheduleLesson", item);
}

function cancelLessons(repeating) {
    router.put(
        route("lesson.delete", props.lesson.id),
        { cancelRepeating: repeating },
        {
            preserveState: true,
            preserveScroll: true,
            onSuccess: () => {
                handleSuccess();
            },
            onError: (errors) => {
                console.log("Form returned errors" + JSON.stringify(errors));
            },
        }
    );
}

function reset(success) {
    if (success) emit("success", success);
}

function copyToClipboard(text) {
    navigator.clipboard.writeText(text);
    snack.value = "Booking link copied to clipboard!";
}
</script>

<template>
    <v-container class="lesson-detail-actions">
        <!-- Action Row -->
        <v-row no-gutters justify="start" align="center" class="gap-2">
            <v-btn
                v-for="action in actions.filter((e) => !e.hidden())"
                :key="action.title"
                :disabled="action.disabled()"
                rounded
                outlined
                small
                color="accent"
                :href="action.link"
                @click="action.method()"
                ><v-icon small class="mr-1">{{ action.icon }}</v-icon
                >{{ action.title }}</v-btn
            >
            <v-spacer />
            <v-menu offset-y left>
                <template v-slot:activator="{ on, attrs }">
                    <v-btn
                        v-bind="attrs"
                        v-on="on"
                        icon
                        color="dark"
                        @click="showLessonOptions = !showLessonOptions"
                        ><v-icon v-bind="attrs" v-on="on">more_vert</v-icon>
                    </v-btn>
                </template>
                <v-list>
                    <v-list-item
                        v-for="item in menuItems"
                        :key="item.title"
                        :disabled="item.disabled()"
                        @click="item.method()"
                    >
                        <v-list-item-title>{{ item.title }}</v-list-item-title>
                    </v-list-item>
                </v-list>
            </v-menu>
        </v-row>

        <!-- Forms -->
        <div v-for="(form, idx) in forms" :key="idx">
            <SimpleForm
                v-if="form.show"
                :title="form.title() ?? 'Are you sure?'"
                :subtitle="form.subtitle() ?? ''"
                :submit="form.submit"
                @reset="reset($event)"
            >
                <template #body>
                    <SoliTeachers
                        v-if="form.slot === 'substituteTeacher'"
                        v-model="form.submit.data.user_id"
                        autocomplete
                        :returnObject="false"
                        :filterTeacher="lesson.teacher.id"
                        :filterLocation="auth.user.selected_location.id"
                        :rules="[(v) => !!v || v === 0 || 'Item is required']"
                        :all="true"
                        hideDetails="auto"
                    />
                    <v-radio-group
                        v-if="form.slot === 'confirmDelete'"
                        v-model="form.submit.data.cancelRepeating"
                        :rules="[(v) => v !== null || 'Item is required']"
                        class="mt-0"
                    >
                        <v-radio
                            :value="true"
                            label="This and Following Lessons"
                        ></v-radio>
                        <v-radio
                            :value="false"
                            :disabled="isRepeating && lesson.type !== 'Hold'"
                            label="This Lesson"
                        ></v-radio>
                    </v-radio-group>
                    <SoliText
                        v-if="form.slot === 'editCapacity'"
                        number
                        v-model="form.submit.data.capacity"
                        label="Student Capacity"
                        hide-details="auto"
                        icon="people"
                        :min="1"
                        :max="100"
                        :rules="[
                            (val) => !!val || 'This field is required',
                            (val) =>
                                val >= 1 || 'Capacity must be greater than 1',
                            (val) =>
                                val <= 100 || 'Capacity must be less than 100',
                        ]"
                        :validateOnBlur="false"
                    />
                </template>
            </SimpleForm>
        </div>

        <!-- Attendance Notes -->
        <LessonAttendanceNotes
            v-if="showAttendanceNotes"
            :student="lesson.student"
            :lessonNote="getLessonNote"
            :practiceNote="getPracticeNote"
            @reset="showAttendanceNotes = false"
        />

        <SoliSnack v-if="snack" :text="snack" :time="3000" />
    </v-container>
</template>
